import React, { useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import Tooltip from '@atlaskit/tooltip';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { useIntl } from '@atlassian/jira-intl';
import {
	useBackButtonUrl,
	useIssueNavigatorData,
} from '@atlassian/jira-issue-context-service/src/main';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/jira-router';
import { isJSMErrorBoundaryIssueNavigatorEnabled } from '../../../feature-flags';
import messages from './messages';
import { IssueNavigatorWrapper, ButtonsSpacer } from './styled';

const FireNavigatorShownAnalyticsEvent = ({ issueKey }: { issueKey: string }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'issueViewHeaderIssueNavigationButtons shown');
	}, [createAnalyticsEvent, issueKey]);

	return null;
};

const IssueNavigatorInner = () => {
	const issueNavigatorData = useIssueNavigatorData();
	const backButtonUrl = useBackButtonUrl();
	const { formatMessage } = useIntl();

	if (!issueNavigatorData || !backButtonUrl) {
		return null;
	}

	const { issueKey, issueKeys, knownIssueKeys, issuesCount } = issueNavigatorData;
	const index = knownIssueKeys.indexOf(issueKey);

	if (index === -1) {
		return null;
	}

	const createHandleOnClick =
		(actionSubjectId: string) =>
		(_event: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, actionSubjectId);
		};

	const canNavigateNext = index < knownIssueKeys.length - 1;
	const isLastIssue = issueKeys.indexOf(issueKey) === issuesCount - 1;
	const nextContent = !isLastIssue ? formatMessage(messages.canNotNavigateNextIssueTooltip) : '';
	const nextToolTipContent = canNavigateNext
		? formatMessage(messages.nextIssueIssueTooltip, {
				issueKey: knownIssueKeys[index + 1],
			})
		: nextContent;

	const canNavigatePrevious = index > 0;

	const isFirstIssue = issueKeys.indexOf(issueKey) === 0;
	const previousContent = !isFirstIssue
		? formatMessage(messages.canNotNavigatePrevioustIssueTooltip)
		: '';
	const previousToolTipContent = canNavigatePrevious
		? formatMessage(messages.previousIssueTooltip, {
				issueKey: knownIssueKeys[index - 1],
			})
		: previousContent;

	return (
		<ContextualAnalyticsData
			attributes={{
				navIssuesCount: issuesCount,
				navCurrentIssueIndex: issueKeys.indexOf(issueKey),
				canNavigatePrevious,
				canNavigateNext,
				isLastNavigationIssue: isLastIssue,
			}}
		>
			<FireNavigatorShownAnalyticsEvent issueKey={issueKey} />
			<IssueNavigatorWrapper>
				<Tooltip
					content={previousToolTipContent}
					position="bottom"
					testId="issue-view-foundation.header.header-breadcrumbs.issue-navigator.navigate-previous-button-tooltip"
				>
					<Button
						isDisabled={!canNavigatePrevious}
						href={`${backButtonUrl}/${knownIssueKeys[index - 1]}`}
						onClick={createHandleOnClick('navigatePreviousIssueClicked')}
						component={Link}
						data-testId="issue-view-foundation.header.header-breadcrumbs.issue-navigator.navigate-previous-button"
						aria-label={canNavigatePrevious ? previousToolTipContent : undefined}
						interactionName="auto-generated.jira-issue-view-foundation.header.header-breadcrumbs.issue-navigator.button"
					>
						<ChevronUpIcon size="medium" label="" />
					</Button>
				</Tooltip>
				<ButtonsSpacer />
				<Tooltip
					content={nextToolTipContent}
					position="bottom"
					testId="issue-view-foundation.header.header-breadcrumbs.issue-navigator.navigate-next-button-tooltip"
				>
					<Button
						isDisabled={!canNavigateNext}
						href={`${backButtonUrl}/${knownIssueKeys[index + 1]}`}
						onClick={createHandleOnClick('navigateNextIssueClicked')}
						component={Link}
						data-testId="issue-view-foundation.header.header-breadcrumbs.issue-navigator.navigate-next-button"
						aria-label={canNavigateNext ? nextToolTipContent : undefined}
						interactionName="auto-generated.jira-issue-view-foundation.header.header-breadcrumbs.issue-navigator.button1"
					>
						<ChevronDownIcon size="medium" label="" />
					</Button>
				</Tooltip>
			</IssueNavigatorWrapper>
		</ContextualAnalyticsData>
	);
};

export const IssueNavigator = () =>
	isJSMErrorBoundaryIssueNavigatorEnabled() ? (
		<JSErrorBoundary
			id="jsm-issue-navigator-error-boundary"
			packageName="jiraServiceDeskIssueView"
			teamName="bento"
			fallback="flag"
		>
			<IssueNavigatorInner />
		</JSErrorBoundary>
	) : (
		<IssueNavigatorInner />
	);
